import { z } from 'zod';
import { lotOriginSchema } from './lot';
import { warehouseZoneCodeSchema } from './warehouse';

// =========================================================
// DOCUMENT SAP (Spostamento A Produzione)
// Riferimento: docs/decisions/0007-sap-beta-warehouse-zone.md
// Riga SAP: lottoCode (puo creare nuovo Lot se non esiste) + productId opzionale
// (default DA_CATEGORIZZARE finche un PSC successivo non lo specifica) + qty + note.
// Close: per ogni riga -> crea/aggiorna Lot in zona PRODUZIONE + StockMovement IN.
// =========================================================

// Convenzione: la riga puo
// (a) referenziare un Lot esistente via lotId -> SAP sposta currentZone a PRODUZIONE (no qty change se gia li')
// (b) creare un nuovo Lot via lotCode + lotOrigin (no lotId) -> Lot creato direttamente in PRODUZIONE
const sapLineBase = z.object({
  // Opzione A: lotto esistente
  lotId: z.string().uuid().nullable().optional(),
  // Opzione B: nuovo lotto da SAP
  lotCode: z.string().trim().min(1).max(64).regex(/^[A-Z0-9_-]+$/i).nullable().optional(),
  lotOrigin: lotOriginSchema.optional(),
  // Prodotto della riga: default DA_CATEGORIZZARE finche un PSC successivo non lo specifica.
  productId: z.string().uuid().nullable().optional(),
  qty: z.coerce.number().positive('Qty SAP > 0'),
  // Nota libera Drive: sigle destinazione produttiva (es. "BB", "MELON/AMN", "WH/GEL/DC/SM")
  note: z.string().max(500).nullable().optional(),
});

export const sapLineSchema = sapLineBase.refine(
  (d) => !!d.lotId || !!d.lotCode,
  { message: 'Indicare lotId esistente OPPURE lotCode per nuovo lotto', path: ['lotId'] },
);
export type SapLine = z.infer<typeof sapLineSchema>;

export const sapCreateRequestSchema = z.object({
  draftName: z.string().min(1, 'Nome SAP obbligatorio').max(100),
  responsable: z.string().max(100).nullable().optional(),
  workDate: z.coerce.date(),
  notes: z.string().max(2000).nullable().optional(),
  righe: z.array(sapLineSchema).default([]),
});
export type SapCreateRequest = z.infer<typeof sapCreateRequestSchema>;

export const sapUpdateRequestSchema = sapCreateRequestSchema.partial();
export type SapUpdateRequest = z.infer<typeof sapUpdateRequestSchema>;

export const sapCloseRequestSchema = z.object({}).strict();
export type SapCloseRequest = z.infer<typeof sapCloseRequestSchema>;

// Response

const documentStatusSchema = z.enum(['DRAFT', 'CLOSED', 'SUPERSEDED']);

export const sapLineResponseSchema = z.object({
  id: z.string(),
  lineNumber: z.number().int().positive(),
  lotId: z.string().nullable(),
  productId: z.string().nullable(),
  qty: z.string(),
  note: z.string().nullable(),
  lotCode: z.string().nullable(),
  productCode: z.string().nullable(),
  productName: z.string().nullable(),
});
export type SapLineResponse = z.infer<typeof sapLineResponseSchema>;

export const sapResponseSchema = z.object({
  id: z.string(),
  type: z.literal('SAP'),
  code: z.string().nullable(),
  draftName: z.string().nullable(),
  workDate: z.string(),
  periodId: z.string(),
  period: z
    .object({
      year: z.number().int(),
      month: z.number().int(),
      status: z.enum(['OPEN', 'CLOSED']),
    })
    .optional(),
  status: documentStatusSchema,
  locked: z.boolean(),
  responsable: z.string().nullable(),
  notes: z.string().nullable(),
  createdById: z.string(),
  closedById: z.string().nullable(),
  createdAt: z.string(),
  closedAt: z.string().nullable(),
  updatedAt: z.string(),
  righe: z.array(sapLineResponseSchema),
  totQtyG: z.string(),
  destZoneCode: warehouseZoneCodeSchema, // per ora hardcoded PRODUZIONE
});
export type SapResponse = z.infer<typeof sapResponseSchema>;

export const sapListQuerySchema = z.object({
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(200).default(50),
  status: documentStatusSchema.optional(),
  periodId: z.string().uuid().optional(),
  search: z.string().trim().min(1).max(100).optional(),
  workDateFrom: z.coerce.date().optional(),
  workDateTo: z.coerce.date().optional(),
  format: z.enum(['json', 'xlsx']).default('json'),
});
export type SapListQuery = z.infer<typeof sapListQuerySchema>;

export const sapListItemSchema = z.object({
  id: z.string(),
  type: z.literal('SAP'),
  code: z.string().nullable(),
  draftName: z.string().nullable(),
  workDate: z.string(),
  status: documentStatusSchema,
  responsable: z.string().nullable(),
  closedAt: z.string().nullable(),
  totQtyG: z.string(),
  righeCount: z.number().int().nonnegative(),
});
export type SapListItem = z.infer<typeof sapListItemSchema>;

export const sapListResponseSchema = z.object({
  items: z.array(sapListItemSchema),
  total: z.number().int().nonnegative(),
  page: z.number().int().positive(),
  pageSize: z.number().int().positive(),
});
export type SapListResponse = z.infer<typeof sapListResponseSchema>;
